/**
 * Vue Composable for Permission Checking
 * Provides reactive permission checking for Vue components
 */

import { computed } from '@vue/reactivity';
import type { ComputedRef } from '@vue/reactivity';
import { useAuthStore } from '@/stores';

import permissionService, { PermissionType } from '@/services/permission.service';
import type { User } from '@/types';

export interface UsePermissionsReturn {
  // Permission checking functions
  hasPermission: (permission: PermissionType | string) => ComputedRef<boolean>;
  hasAnyPermission: (permissions: Array<PermissionType | string>) => ComputedRef<boolean>;
  hasAllPermissions: (permissions: Array<PermissionType | string>) => ComputedRef<boolean>;
  // Project-scoped variants (optional projectId)
  hasPermissionForProject: (permission: PermissionType | string, projectId?: string | number) => ComputedRef<boolean>;
  hasAnyPermissionForProject: (permissions: Array<PermissionType | string>, projectId?: string | number) => ComputedRef<boolean>;
  hasAllPermissionsForProject: (permissions: Array<PermissionType | string>, projectId?: string | number) => ComputedRef<boolean>;

  isAuthenticated: { value: boolean };
  
  // Permission constants
  PermissionType: typeof PermissionType;
}

export function usePermissions(): UsePermissionsReturn {
  const authStore = useAuthStore();
  
  // Get current user reactively
  const user = computed(() => authStore.getUser as User | null);
  const isAuthenticated = computed(() => authStore.isAuthenticated as boolean);

  // Permission checking functions
  const hasPermission = (permission: PermissionType | string) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasPermission(permission);
    });
  };

  const hasPermissionForProject = (permission: PermissionType | string, projectId?: string | number) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasPermission(permission, projectId);
    });
  };

  const hasAnyPermission = (permissions: Array<PermissionType | string>) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasAnyPermission(permissions);
    });
  };

  const hasAnyPermissionForProject = (permissions: Array<PermissionType | string>, projectId?: string | number) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasAnyPermission(permissions, projectId);
    });
  };

  const hasAllPermissions = (permissions: Array<PermissionType | string>) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasAllPermissions(permissions);
    });
  };

  const hasAllPermissionsForProject = (permissions: Array<PermissionType | string>, projectId?: string | number) => {
    return computed(() => {
      if (!isAuthenticated.value) return false;
      return permissionService.hasAllPermissions(permissions, projectId);
    });
  };

  return {
    // Permission checking functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasPermissionForProject,
    hasAnyPermissionForProject,
    hasAllPermissionsForProject,
    
    // Utility
    isAuthenticated,
    
    // Permission constants for easy access
    PermissionType
  };
}
